import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PhotoIcon, DocumentIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { uploadFile, getFileUrl, deleteFile } from '../../utils/api';

const FileUploader = ({ 
  files = [], 
  onChange,
  isReadOnly = false,
  multiple = true,
  allowedFileTypes = "image/png, image/jpeg, image/gif, application/pdf"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // Track newly uploaded files separately to handle deletions correctly
  const [newlyUploadedFiles, setNewlyUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  // Clear error when new files are added
  const clearError = useCallback(() => {
    if (uploadError) {
      setUploadError(null);
    }
  }, [uploadError]);
  
  // Process files (common function for both drag-drop and file input)
  const processFiles = useCallback(async (filesList) => {
    if (!filesList || filesList.length === 0) return;
    
    clearError();
    setIsUploading(true);
    
    try {
      const uploadPromises = Array.from(filesList).map(file => uploadFile(file));
      const uploadResults = await Promise.all(uploadPromises);
      
      // Extract file info from upload results
      const newFiles = uploadResults.map(result => result.file);
      
      // Track these as newly uploaded files
      setNewlyUploadedFiles(prev => [...prev, ...newFiles.map(file => file.uuid)]);
      
      // Update files list with new files
      onChange([...files, ...newFiles]);
    } catch (error) {
      console.error('File upload failed:', error);
      setUploadError(error.message || 'Failed to upload file(s)');
    } finally {
      setIsUploading(false);
      // Clear the file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [files, onChange, clearError]);
  
  // Handle file selection from file input
  const handleFileChange = useCallback((e) => {
    processFiles(e.target.files);
  }, [processFiles]);
  
  // Handle file deletion
  const handleDeleteFile = useCallback(async (fileToDelete) => {
    try {
      // Only delete files from server if they were newly uploaded in this session
      // or if they're being removed from a new record (not an edit)
      if (fileToDelete.uuid && 
          (newlyUploadedFiles.includes(fileToDelete.uuid))) {
        await deleteFile(fileToDelete.uuid);
        // Remove from newly uploaded files list
        setNewlyUploadedFiles(prev => prev.filter(uuid => uuid !== fileToDelete.uuid));
      }
      
      // Always remove file from the UI list
      const updatedFiles = files.filter(file => file.uuid !== fileToDelete.uuid);
      onChange(updatedFiles);
    } catch (error) {
      console.error('Error deleting file:', error);
      setUploadError(error.message || 'Failed to delete file');
    }
  }, [files, onChange, newlyUploadedFiles]);
  
  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set isDragging to false if we're leaving the dropzone itself
    // not its children
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // Get the dropped files
    const droppedFiles = e.dataTransfer.files;
    processFiles(droppedFiles);
  }, [processFiles]);
  
  // Set up and clean up drag and drop event listeners
  useEffect(() => {
    const dropZone = dropZoneRef.current;
    
    if (dropZone && !isReadOnly) {
      dropZone.addEventListener('dragenter', handleDragEnter);
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);
      
      // Clean up
      return () => {
        dropZone.removeEventListener('dragenter', handleDragEnter);
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('drop', handleDrop);
      };
    }
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop, isReadOnly]);
  
  // Determine if a file is an image
  const isImageFile = (file) => {
    return file.mime_type && file.mime_type.startsWith('image/');
  };
  
  return (
    <div>
      {/* File upload area */}
      {!isReadOnly && (
        <div className="mt-2">
          <div 
            ref={dropZoneRef}
            className={`flex justify-center rounded-lg border-2 border-dashed px-6 py-6 ${
              isDragging 
                ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' 
                : 'border-gray-900/25 dark:border-gray-700'
            }`}
          >
            <div className="text-center">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
              <div className="mt-4 flex text-sm/6 text-gray-600 dark:text-gray-400">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-semibold text-indigo-600 dark:text-indigo-400 focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 focus-within:outline-hidden hover:text-indigo-500"
                >
                  <span>Upload a file</span>
                  <input 
                    id="file-upload" 
                    name="file-upload" 
                    type="file" 
                    className="sr-only"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    accept={allowedFileTypes}
                    multiple={multiple}
                    disabled={isUploading}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs/5 text-gray-600 dark:text-gray-500">PNG, JPG, GIF, PDF up to 10MB</p>
              
              {isUploading && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 text-indigo-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </div>
                </div>
              )}
              
              {uploadError && (
                <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {uploadError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* File list */}
      {files.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-300">
            Attached Files ({files.length})
          </h4>
          <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
            {files.map((file, index) => (
              <li key={file.uuid || index} className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                <div className="flex w-0 flex-1 items-center">
                  {isImageFile(file) ? (
                    <PhotoIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  ) : (
                    <DocumentIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  )}
                  {file.uuid ? (
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(getFileUrl(file.uuid), '_blank');
                      }}
                      className="ml-2 flex-1 w-0 truncate font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 hover:underline text-left cursor-pointer"
                      title="View file in new tab"
                    >
                      {file.original_filename || file.filename}
                    </button>
                  ) : (
                    <span className="ml-2 flex-1 w-0 truncate">
                      {file.original_filename || file.filename}
                    </span>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 flex space-x-2">
                  {/* Download button */}
                  {file.uuid && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        // Build URL with download parameter
                        const downloadUrl = `${getFileUrl(file.uuid)}?download=true`;
                        
                        // Use a fetch request for the download to maintain the session
                        fetch(downloadUrl, {
                          method: 'GET',
                          credentials: 'include', // Important for sending cookies with the request
                        })
                          .then(response => {
                            // Get the filename from the Content-Disposition header if available
                            const contentDisposition = response.headers.get('content-disposition');
                            let filename = file.original_filename || file.filename;
                            
                            if (contentDisposition) {
                              const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                              if (fileNameMatch && fileNameMatch[1]) {
                                filename = fileNameMatch[1].replace(/['"]/g, '');
                              }
                            }
                            
                            return response.blob().then(blob => {
                              // Create a download link and click it
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', filename);
                              document.body.appendChild(link);
                              link.click();
                              link.parentNode.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            });
                          })
                          .catch(error => {
                            console.error('Download failed:', error);
                            alert('Failed to download file. Please try again.');
                          });
                      }}
                      className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                      title="Download file"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  )}
                  
                  {/* Delete button - only show if not read-only */}
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(file)}
                      className="font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUploader;